// =============================================================================
// StageColumn: one stage lane on the Track tab's board — the header (stage
// label + count) + its application cards, and the native-HTML5-drag drop
// target a card lands on to move stage. Mirrors RoleGroupSection.tsx's
// structure (header, card list, a drop affordance) exactly — same house
// pattern the Refine tab's corpus canvas already established, applied to
// application stages instead of roles/eras.
// =============================================================================

import type { Application, ApplicationStatus, DraftOutreachResult } from '../lib/api';
import { STAGE_EMPTY_TEXT, type StageColumn as StageColumnData } from '../lib/applicationBoard';
import { Card } from './Card';
import { StageCard } from './StageCard';
import clsx from './clsx';
import styles from './StageColumn.module.css';

export interface StageColumnProps {
  column: StageColumnData;
  draggingId: string | null;
  pendingId: string | null;
  isDropTarget: boolean;
  onDragStartCard: (id: string) => void;
  onDragEndCard: () => void;
  onDragOverColumn: () => void;
  onDragLeaveColumn: () => void;
  /** Fires when a card is dropped on this column — the caller resolves
   *  whether it is a no-op (dropped on its own stage) or a real move. */
  onDropCard: (applicationId: string, toStatus: ApplicationStatus) => void;
  onMoveStage: (id: string, toStatus: ApplicationStatus) => void;
  draftingFollowUpId: string | null;
  onDraftFollowUp: (applicationId: string, jobId: string) => void;
  draftResultByApplicationId: Map<string, DraftOutreachResult>;
  /** GAP 4 — threaded straight through to every StageCard, same passthrough
   *  shape as onMoveStage above. */
  onSaveNotes: (id: string, notes: string) => Promise<void>;
  savingNotesId: string | null;
}

export function StageColumn({
  column,
  draggingId,
  pendingId,
  isDropTarget,
  onDragStartCard,
  onDragEndCard,
  onDragOverColumn,
  onDragLeaveColumn,
  onDropCard,
  onMoveStage,
  draftingFollowUpId,
  onDraftFollowUp,
  draftResultByApplicationId,
  onSaveNotes,
  savingNotesId,
}: StageColumnProps) {
  return (
    <section
      className={clsx(styles.column, isDropTarget && styles.dropTarget)}
      onDragOver={(e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        onDragOverColumn();
      }}
      onDragLeave={onDragLeaveColumn}
      onDrop={(e) => {
        e.preventDefault();
        const applicationId = e.dataTransfer.getData('text/plain');
        if (applicationId) onDropCard(applicationId, column.status);
      }}
    >
      <div className={styles.header}>
        <div className={styles.label}>{column.label}</div>
        <div className={styles.count}>{column.applications.length}</div>
      </div>

      {column.applications.length === 0 ? (
        <Card className={styles.emptyCard}>
          <div className={styles.emptyText}>{STAGE_EMPTY_TEXT[column.status]}</div>
        </Card>
      ) : (
        <div className={styles.cardStack}>
          {column.applications.map((application: Application) => (
            <StageCard
              key={application.id}
              application={application}
              dragging={draggingId === application.id}
              pending={pendingId === application.id}
              onDragStart={onDragStartCard}
              onDragEnd={onDragEndCard}
              onMoveStage={onMoveStage}
              draftingFollowUp={draftingFollowUpId === application.id}
              onDraftFollowUp={(jobId) => onDraftFollowUp(application.id, jobId)}
              draftResult={draftResultByApplicationId.get(application.id) ?? null}
              onSaveNotes={onSaveNotes}
              savingNotes={savingNotesId === application.id}
            />
          ))}
        </div>
      )}
    </section>
  );
}
